import { ProfileModal } from "@/features/profile/ProfileModal";
import { useWhoamiRetrieveQuery } from "@/services/generatedApi";
import { useAppSelector } from "@/services/store";
import { Avatar } from "@/ui-components/Avatar";
import { NotificationButton } from "@/ui-components/components/UserMenu/components/NotificationButton";
import { LanguageSelector } from "@/ui-components/components/i18n/LanguageSelector";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { IconButton, Menu, MenuItem, MenuProps, Tooltip, Typography } from "@mui/material";
import { useOktaAuth } from "@okta/okta-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const StyledMenu = ({ sx = {}, ...props }: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    keepMounted
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
    sx={{
      mt: "45px",
      ...sx,
    }}
  />
);

export const UserMenu = () => {
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [anchorElHelp, setAnchorElHelp] = React.useState<null | HTMLElement>(null);
  const [modalIsOpen, setModalIsOpen] = React.useState(false);
  const { data } = useWhoamiRetrieveQuery();
  const { oktaAuth } = useOktaAuth();
  const { t } = useTranslation();
  const name = data ? `${data.first_name} ${data.last_name}` : "";
  const userId = useAppSelector(state => state.auth.userId);
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleOpenHelpMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElHelp(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleCloseHelpMenu = () => {
    setAnchorElHelp(null);
  };

  const toggleModal = () => {
    setModalIsOpen(val => !val);
    handleCloseUserMenu();
  };

  const doLogout = () => {
    oktaAuth.signOut();
  };
  return (
    <>
      <div className="flex gap-4 items-center">
        <LanguageSelector />
        <NotificationButton />
        <Tooltip title={t("general.Help")}>
          <IconButton
            sx={{
              height: "36px",
              width: "36px",
              borderRadius: "8px",
              p: 0,
              "&:hover": {
                backgroundColor: "#536059",
              },
            }}
            onClick={handleOpenHelpMenu}
          >
            <HelpOutlineIcon sx={{ color: "#FFFFFF", width: "34px", height: "34px" }} />
          </IconButton>
        </Tooltip>
        <StyledMenu
          id="menu-help"
          anchorEl={anchorElHelp}
          open={Boolean(anchorElHelp)}
          onClose={handleCloseHelpMenu}
          sx={{ a: { textDecoration: "none", color: "inherit" } }}
        >
          <MenuItem>
            <Link to={`${import.meta.env.VITE_HELP_URL}`} target="_blank" rel="noreferrer">
              {t("general.Documentation")}
            </Link>
          </MenuItem>
          <MenuItem>
            <Link to={import.meta.env.VITE_SUPPORT_URL} target="_blank" rel="noreferrer">
              {t("general.SupportPortal")}
            </Link>
          </MenuItem>
        </StyledMenu>
        <Tooltip title={t("general.Open_settings")}>
          <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} data-testid="open-user-settings">
            <Avatar
              name={name || ""}
              userId={userId}
              sx={{
                width: 32,
                height: 32,
                mr: "8px",
                fontSize: "18px",
                color: "var(--dark-gray-900)",
              }}
            />
            <Typography
              sx={{
                fontSize: "15px",
                fontStyle: "normal",
                fontWeight: "600",
                lineHeight: "22px",
                color: "var(--soft-black-gray-scale-50)",
                textAlign: "center",
                pr: "8px",
              }}
            >
              {name}
            </Typography>
            <KeyboardArrowDownIcon sx={{ color: "var(--soft-black-gray-scale-50)", width: 32, height: 32 }} />
          </IconButton>
        </Tooltip>
        <StyledMenu id="menu-appbar" anchorEl={anchorElUser} open={Boolean(anchorElUser)} onClose={handleCloseUserMenu}>
          <MenuItem onClick={toggleModal}>{t("profile.Profile")}</MenuItem>
          <MenuItem onClick={handleCloseUserMenu}>
            <Typography
              onClick={doLogout}
              sx={{
                textAlign: "center",
              }}
            >
              {t("profile.Logout")}
            </Typography>
          </MenuItem>
        </StyledMenu>
      </div>
      <ProfileModal isOpen={modalIsOpen} toggleIsOpen={toggleModal} />
    </>
  );
};
